/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The interval used to specify repeating events
 */
export const Frequency = {
  Secondly: "Secondly",
  Minutely: "Minutely",
  Hourly: "Hourly",
  Daily: "Daily",
  Weekly: "Weekly",
  Monthly: "Monthly",
  Yearly: "Yearly",
} as const;
/**
 * The interval used to specify repeating events
 */
export type Frequency = OpenEnum<typeof Frequency>;

/**
 * The recurrence rule of a schedule event
 */
export type ScheduleRecurrenceRule = {
  /**
   * A comma separated list of days of the week the event should occur.
   */
  byDay?: Array<string> | undefined;
  /**
   * A comma separated list of hours within a day. Valid values are 0 to 23. Should not be specified if associated StartDate is a date value type
   */
  byHour?: Array<number> | undefined;
  /**
   * A comma separated list of minutes within an hour. Valid values are 0 to 59. Should not be specified if associated StartDate is a date value type
   */
  byMinute?: Array<number> | undefined;
  /**
   * A comma separated list of months the event should occur. Valid values are 1 to 12.
   */
  byMonth?: Array<number> | undefined;
  /**
   * A comma separated list of days of the month the event should occur. Valid values are -31 to -1 or 1 to 31. Should not be specified if Frequency is Weekly.
   */
  byMonthDay?: Array<number> | undefined;
  /**
   * A comma separated list of seconds within a minute. Valid values are 0 to 59. Should not be specified if associated StartDate is a date value type
   */
  bySecond?: Array<number> | undefined;
  /**
   * A positive integer that specifies the number of times the recurrence rule should repeat, with the first occurrence being the StartDate. Cannot be specified if Until is specified.
   */
  count?: number | undefined;
  /**
   * The interval used to specify repeating events
   */
  frequency: Frequency;
  /**
   * Interval is a positive integer representing at which intervals the recurrence rule repeats
   */
  interval?: number | undefined;
  /**
   * RFC3339 formatted time in UTC
   */
  until?: Date | undefined;
};

/** @internal */
export const Frequency$inboundSchema: z.ZodType<
  Frequency,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(Frequency);
/** @internal */
export const Frequency$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  Frequency
> = openEnums.outboundSchema(Frequency);

/** @internal */
export const ScheduleRecurrenceRule$inboundSchema: z.ZodType<
  ScheduleRecurrenceRule,
  z.ZodTypeDef,
  unknown
> = z.object({
  by_day: types.optional(z.array(types.string())),
  by_hour: types.optional(z.array(types.number())),
  by_minute: types.optional(z.array(types.number())),
  by_month: types.optional(z.array(types.number())),
  by_month_day: types.optional(z.array(types.number())),
  by_second: types.optional(z.array(types.number())),
  count: types.optional(types.number()),
  frequency: Frequency$inboundSchema,
  interval: types.optional(types.number()),
  until: types.optional(types.date()),
}).transform((v) => {
  return remap$(v, {
    "by_day": "byDay",
    "by_hour": "byHour",
    "by_minute": "byMinute",
    "by_month": "byMonth",
    "by_month_day": "byMonthDay",
    "by_second": "bySecond",
  });
});
/** @internal */
export type ScheduleRecurrenceRule$Outbound = {
  by_day?: Array<string> | undefined;
  by_hour?: Array<number> | undefined;
  by_minute?: Array<number> | undefined;
  by_month?: Array<number> | undefined;
  by_month_day?: Array<number> | undefined;
  by_second?: Array<number> | undefined;
  count?: number | undefined;
  frequency: string;
  interval?: number | undefined;
  until?: string | undefined;
};

/** @internal */
export const ScheduleRecurrenceRule$outboundSchema: z.ZodType<
  ScheduleRecurrenceRule$Outbound,
  z.ZodTypeDef,
  ScheduleRecurrenceRule
> = z.object({
  byDay: z.array(z.string()).optional(),
  byHour: z.array(z.number().int()).optional(),
  byMinute: z.array(z.number().int()).optional(),
  byMonth: z.array(z.number().int()).optional(),
  byMonthDay: z.array(z.number().int()).optional(),
  bySecond: z.array(z.number().int()).optional(),
  count: z.number().int().optional(),
  frequency: Frequency$outboundSchema,
  interval: z.number().int().optional(),
  until: z.date().transform(v => v.toISOString()).optional(),
}).transform((v) => {
  return remap$(v, {
    byDay: "by_day",
    byHour: "by_hour",
    byMinute: "by_minute",
    byMonth: "by_month",
    byMonthDay: "by_month_day",
    bySecond: "by_second",
  });
});

export function scheduleRecurrenceRuleToJSON(
  scheduleRecurrenceRule: ScheduleRecurrenceRule,
): string {
  return JSON.stringify(
    ScheduleRecurrenceRule$outboundSchema.parse(scheduleRecurrenceRule),
  );
}
export function scheduleRecurrenceRuleFromJSON(
  jsonString: string,
): SafeParseResult<ScheduleRecurrenceRule, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ScheduleRecurrenceRule$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ScheduleRecurrenceRule' from JSON`,
  );
}
